import { compile, evaluateCompiled, type EvaluationResult, type Scope } from '../expr';

export interface ExpressionValue {
  readonly expr: string;
}

export type Value = number | ExpressionValue;

/** How callers supply a value: a number, or text that is a number or an expression. */
export type ValueInput = number | string;

export type ValueResult = EvaluationResult;

export const isExpression = (value: Value): value is ExpressionValue => typeof value !== 'number';

export function valueSource(value: Value): string {
  return isExpression(value) ? value.expr : formatNumber(value);
}

export function formatNumber(value: number, decimals = 4): string {
  if (!Number.isFinite(value)) return String(value);
  return String(Number(value.toFixed(decimals)));
}

export function toValue(input: ValueInput): Value {
  return typeof input === 'number' ? input : parseValue(input);
}

export function parseValue(input: string): Value {
  const trimmed = input.trim();
  if (/^[+-]?(?:[0-9]*\.?[0-9]+)(?:[eE][+-]?[0-9]+)?$/.test(trimmed)) return Number(trimmed);
  return { expr: trimmed };
}

export function evaluateValue(value: Value, scope: Scope): ValueResult {
  if (!isExpression(value)) return { value, error: null };

  const compiled = compile(value.expr);
  if (typeof compiled === 'string') return { value: null, error: compiled };
  return evaluateCompiled(compiled, scope);
}
